import React, { useRef, useState, useEffect, useId } from 'react';
import Image from 'next/legacy/image';

import { CFColorTokenProp } from '../../../theme/tokens/contentful-color';

import { BannerMessage } from '../BannerMessage';
import { GetStartedInput } from './GetStartedInput';
import { GetStartedButton } from './GetStartedButton';

import { WidgetProps, DEFAULT_BUTTON_TEXT, SharedProps } from '../shared';
import RichText from '~/components/RichText';

import blackMiniArrow from '@/images/mini-arrow-black.svg';
import whiteMiniArrow from '@/images/mini-arrow-white.svg';
import blackArrow from '@/images/arrow-black.svg';
import whiteArrow from '@/images/arrow-white.svg';
import { Block, BLOCKS, Inline } from '@contentful/rich-text-types';
import { RenderNode } from '@contentful/rich-text-react-renderer';

export interface GetStartedWidgetProps extends WidgetProps {
  onStartClick?: () => void;
}

const ctaOverrideOptions: RenderNode = {
  [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => {
    return (
      <p className="w-fit items-center justify-center text-[10px] leading-4">
        {children}
      </p>
    );
  }
};

export function GetStartedWidget({
  autocompleteAttribute,
  buttonColor = 'ginger',
  buttonHoverColor = 'green',
  buttonText = DEFAULT_BUTTON_TEXT,
  buttonContent,
  isScrolled = false,
  label,
  privacyDisclaimerDropdown,
  errorMessage,
  errorPosition,
  showErrorBanner,
  inputProps
}: WidgetProps & SharedProps) {
  const [isStartClicked, setIsStartClicked] = useState(false);
  const [imgSrc, setImgSrc] = useState('');

  const getStartInputRef = useRef<any>();
  const errorId = useId();
  const errorRef = useRef<HTMLDivElement>(null);
  const tabIndex = isStartClicked ? 0 : -1;

  // Focus management: Move focus to input when error appears
  useEffect(() => {
    if (errorMessage && isStartClicked) {
      setTimeout(() => {
        if (getStartInputRef.current) {
          getStartInputRef.current.focus();
        }
      }, 200);
    }
  }, [errorMessage, isStartClicked]);

  useEffect(() => {
    const useLightArrow = ['blackberry', 'sweetmint'].includes(
      buttonColor as string
    );
    const arrow = useLightArrow ? whiteArrow : blackArrow;
    const miniArrow = useLightArrow ? whiteMiniArrow : blackMiniArrow;
    setImgSrc(isScrolled ? miniArrow : arrow);
  }, [buttonColor]);

  return isStartClicked ? (
    <div
      id="flex-wrapper"
      className="relative mb-4 flex h-full w-fit flex-col justify-center self-center"
      style={{
        WebkitTransform: 'translate3d(0, 0, 0)',
        transform: 'translate3d(0, 0, 0)',
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden',
        WebkitOverflowScrolling: 'touch',
        WebkitPerspective: '1000',
        perspective: '1000'
      }}
    >
      <div className="z-[1] flex h-full w-full items-center">
        <GetStartedInput
          {...inputProps}
          ref={getStartInputRef}
          tabIndex={tabIndex}
          isScrolled={isScrolled}
          placeholder={label ?? ''}
          autoComplete={autocompleteAttribute ?? 'tel'}
          borderColor={errorMessage ? '#D31400' : '#3BBFAD'}
          aria-invalid={!!errorMessage}
          aria-describedby={errorMessage ? errorId : undefined}
        />
        <button
          type="submit"
          aria-label="Get started"
          data-testid="get-started-button"
          className={[
            'absolute',
            'right-0',
            'flex',
            'flex-col',
            'justify-center',
            'items-center',
            'h-10',
            'max-h-10',
            'rounded-[8px]',
            'px-2',
            'transition-colors',
            'duration-200',
            'hover:text-transparent',
            'focus:text-transparent',
            'active:text-transparent'
          ].join(' ')}
          style={{
            width: isScrolled ? 32 : 48,
            minWidth: 48,
            margin: isScrolled ? 'auto 2%' : 'auto 8px'
          }}
        >
          <Image src={imgSrc} alt="Get started with Greenlight" />
        </button>
      </div>
      <div
        className="absolute left-0 top-full h-8 w-full overflow-hidden rounded-b-lg transition-all duration-300"
        style={{
          WebkitTransform: 'translate3d(0, 0, 0)',
          transform: 'translate3d(0, 0, 0)',
          WebkitBackfaceVisibility: 'hidden',
          backfaceVisibility: 'hidden'
        }}
      >
        <div className="flex h-full items-center justify-center">
          <RichText
            data={privacyDisclaimerDropdown}
            overrideOptions={ctaOverrideOptions}
          />
        </div>
      </div>

      {showErrorBanner && errorMessage && !privacyDisclaimerDropdown && (
        <div
          ref={errorRef}
          id={errorId}
          role="alert"
          aria-live="polite"
          tabIndex={-1}
          className={[
            'bg-[#035c67]',
            'w-full',
            'min-h-[25px]',
            'text-white',
            'text-center',
            'absolute',
            errorPosition === 'top'
              ? `rounded-t-[20px] ${
                  isScrolled ? 'bottom-[30px]' : 'bottom-[55px]'
                } p-4`
              : `rounded-b-[20px] ${
                  isScrolled ? 'top-[30px]' : 'top-[55px]'
                } p-4`
          ].join(' ')}
          style={{
            WebkitTransform: 'translate3d(0, 0, 0)',
            transform: 'translate3d(0, 0, 0)',
            WebkitBackfaceVisibility: 'hidden',
            backfaceVisibility: 'hidden'
          }}
        >
          {errorMessage}
        </div>
      )}
      {showErrorBanner && errorMessage && !!privacyDisclaimerDropdown && (
        <BannerMessage
          ref={errorRef}
          id={errorId}
          message={errorMessage}
          position={errorPosition}
          isDisclaimerPresent={!!privacyDisclaimerDropdown}
          variant="error"
          role="alert"
          aria-live="polite"
          tabIndex={-1}
        />
      )}
    </div>
  ) : (
    <div>
      <GetStartedButton
        type="submit"
        cfButtonColor={buttonColor as CFColorTokenProp}
        cfButtonHoverColor={buttonHoverColor as CFColorTokenProp}
        isScrolled={isScrolled}
        onClick={(e) => {
          e.preventDefault();
          setIsStartClicked(true);
          return getStartInputRef?.current?.element?.focus();
        }}
        aria-label="Get started"
        data-testid="get-started-button"
      >
        {buttonContent || buttonText}
      </GetStartedButton>
    </div>
  );
}
